import { getFirebaseFunctionsUrl } from '@paperstac/env';
import auth from '@paperstac/firebase/lib/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import { User } from 'firebase/auth';
import * as React from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { trpc } from '../services/trpc';

const REFRESH_MINUTES = 15; // idTokens live for 60 minutes
const firebaseFunctionsUrl = getFirebaseFunctionsUrl();

const TrpcProvider = ({ children }: { children: React.ReactNode }) => {
  const [isClientReady, setClientReady] = React.useState(false);
  const [currentUser, currentUserLoading] = useAuthState(auth);
  const [queryClient] = React.useState(() => new QueryClient());
  const [trpcClient, setTrpcClient] = React.useState(() =>
    trpc.createClient({
      transformer: undefined,
      links: [
        httpBatchLink({
          url: `${firebaseFunctionsUrl}/trpc`,
        }),
      ],
    })
  );

  const refreshTrpcClient = React.useCallback(
    (currentUser: User | null | undefined, currentUserLoading: boolean) => {
      if (currentUserLoading) return;
      if (currentUser) {
        currentUser.getIdToken(true).then((idToken) => {
          setTrpcClient(
            trpc.createClient({
              transformer: undefined,
              links: [
                httpBatchLink({
                  url: `${firebaseFunctionsUrl}/trpc`,
                  headers() {
                    return {
                      authorization: `Bearer ${idToken}`,
                    };
                  },
                }),
              ],
            })
          );
          setClientReady(true);
        });
      } else {
        setTrpcClient(
          trpc.createClient({
            transformer: undefined,
            links: [
              httpBatchLink({
                url: `${firebaseFunctionsUrl}/trpc`,
              }),
            ],
          })
        );
        setClientReady(true);
      }
    },
    [setClientReady, setTrpcClient]
  );

  React.useEffect(() => {
    // Refresh immediately upon user change
    refreshTrpcClient(currentUser, currentUserLoading);
    // Refresh on interval to prevent expiration
    const intervalId = setInterval(
      () => {
        refreshTrpcClient(currentUser, currentUserLoading);
      },
      1000 * 60 * REFRESH_MINUTES
    );
    return () => clearInterval(intervalId);
  }, [currentUser, currentUserLoading, refreshTrpcClient]);

  if (!isClientReady) return null;

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
};

TrpcProvider.displayName = 'TrpcProvider';

export default TrpcProvider;
